import { LocationFeatureFlagSettingName } from '@weave/schema-gen-ts/dist/schemas/payments/messages/location_settings/location_settings.pb';
import * as _ from 'lodash-es';
import { Trans, useTranslation } from '@frontend/i18n';
import {
  PaymentsFeatureSettings,
  useLocationFeatureSettings,
  useLocationFeatureSwitch,
  useMerchants,
  useToggleLocationSurcharge,
} from '@frontend/payments-hooks';
import { theme } from '@frontend/theme';
import { ConfirmationModal, Heading, Text } from '@frontend/design-system';
import { useGeneralSettings } from '../../../hooks/settings/use-general-settings';
import { SurchargeSettingsNoPayments } from '../../Surcharge/surcharge-settings-no-payments';
import { SurchargeSettingsToggle } from '../../Surcharge/surcharge-settings-toggle';
import { SettingsToggle, SettingsToggleProps } from '../atoms/settings-toggle';
import * as styles from '../styles/payment-settings-page.styles';

export const GeneralSettings = () => {
  const { t } = useTranslation('payments', { keyPrefix: 'settings' });

  const {
    canModifyWritebacks,
    ttpAuthToggleAllowedLocations,
    autoReceiptEnabledLocations,
    openDentalWritebacksLocations,
    openDentalCloudWritebacksLocations,
    openDentalLocalWritebacksLocations,
    eaglesoftWritebacksLocations,
    drChronoWritebacksLocations,
    fuseWritebacksLocations,
    dentrixEnterpriseWritebacksLocations,
    dolphinCloudWritebacksLocations, // Import Dolphin Cloud locations
    dolphinWritebacksLocations,
    showWritebacksSection,
    showGeneralSettingsSection,
    isPaymentsBillingManager,
    canManageSurcharging,
    bulkCollectionsEnabledLocations,
    crystalWritebacksLocations,
    athenaOneWritebacksLocations,
    nextechSelectWritebacksLocations,
    nextechPracticeWritebacksLocations,
    dolphinLegacyWritebacksLocations,
  } = useGeneralSettings();

  const { enabledLocations: ttpAuthenticationEnabledLocations } = useLocationFeatureSettings(
    LocationFeatureFlagSettingName.TTP_AUTH_ENABLED
  );

  const {
    featureSettingSwitchProps: ttpAuthenticationProps,
    modalProps: authenticationModalProps,
    onModalConfirm: onConfirmAuthenticationModal,
  } = useLocationFeatureSwitch({
    setting: LocationFeatureFlagSettingName.TTP_AUTH_ENABLED,
    errMsg: t('Error in toggling the TTP Authentication. Please try again.'),
    useConfirmation: ttpAuthToggleAllowedLocations.every((location) =>
      ttpAuthenticationEnabledLocations.includes(location)
    ),
    locationIds: ttpAuthToggleAllowedLocations,
  });

  const {
    featureSettingSwitchProps: surchargeSwitchProps,
    modalProps: surchargeModalProps,
    onModalConfirm: onConfirmSurchargeModal,
    surchargeEnabledLocations,
    locationIdsInPlay: possibleSurcharginLocations,
    percentValue,
    onChangePercentValue,
    combinedMaxSurchargeInfo,
  } = useToggleLocationSurcharge();

  const { featureSettingSwitchProps: receiptSettingsProps } = useLocationFeatureSwitch({
    setting: LocationFeatureFlagSettingName.AUTO_RECEIPT_ENABLED,
    errMsg: t('Error in toggling the receipt settings. Please try again.'),
    locationIds: autoReceiptEnabledLocations,
  });

  /**
   * See if the a location with surcharging has full payments
   */
  const { anyLocationHasFullPayments } = useMerchants(possibleSurcharginLocations);

  if (!showGeneralSettingsSection && !possibleSurcharginLocations.length) {
    return (
      <section css={[styles.form, styles.formWithSections]}>
        <Text color='subdued'>{t('No settings available')}</Text>
      </section>
    );
  }

  // check if any pms has bulkCollectionsEnabled
  const anyBulkCollectionEnabled = Object.values(bulkCollectionsEnabledLocations || {}).some((isEnabled) => isEnabled);

  return (
    <>
      <section css={[styles.form, styles.formWithSections]}>
        {showWritebacksSection && (
          <div className='section section--writebacks'>
            <Heading css={styles.sectionHeading} level={3}>
              {anyBulkCollectionEnabled ? t('Bulk Collections & Writebacks') : t('Writebacks')}
            </Heading>{' '}
            <WritebacksToggle
              pmsName={t('Dolphin Legacy')}
              setting={LocationFeatureFlagSettingName.DOLPHIN_LEGACY_WRITEBACKS_ENABLED}
              locationIds={dolphinLegacyWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.dolphinLegacy}
            />
            <WritebacksToggle
              pmsName={t('Opendental')}
              setting={LocationFeatureFlagSettingName.OPEN_DENTAL_WRITEBACKS_ENABLED}
              locationIds={openDentalWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.openDental}
            />
            <WritebacksToggle
              pmsName={t('Crystal')}
              setting={LocationFeatureFlagSettingName.CRYSTAL_WRITEBACKS_ENABLED}
              locationIds={crystalWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.crystal}
            />
            <WritebacksToggle
              pmsName={t('AthenaOne')}
              setting={LocationFeatureFlagSettingName.ATHENA_ONE_WRITEBACKS_ENABLED}
              locationIds={athenaOneWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.athenaOne}
            />
            <WritebacksToggle
              setting={LocationFeatureFlagSettingName.OPEN_DENTAL_CLOUD_WRITEBACKS_ENABLED}
              pmsName={t('Opendental Cloud')}
              locationIds={openDentalCloudWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.openDentalCloud}
            />
            <WritebacksToggle
              setting={LocationFeatureFlagSettingName.OPEN_DENTAL_LOCAL_WRITEBACKS_ENABLED}
              pmsName={t('Opendental Cloud Local')}
              locationIds={openDentalLocalWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.openDentalLocal}
            />
            <WritebacksToggle
              setting={LocationFeatureFlagSettingName.EAGLESOFT_WRITEBACKS_ENABLED}
              pmsName={t('Eaglesoft')}
              locationIds={eaglesoftWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.eaglesoft}
            />
            <WritebacksToggle
              setting={LocationFeatureFlagSettingName.DR_CHRONO_WRITEBACKS_ENABLED}
              pmsName={t('DrChrono')}
              locationIds={drChronoWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.drChrono}
            />
            <WritebacksToggle
              setting={LocationFeatureFlagSettingName.FUSE_WRITEBACKS_ENABLED}
              pmsName={t('Fuse')}
              locationIds={fuseWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.fuse}
            />
            <WritebacksToggle
              setting={LocationFeatureFlagSettingName.DENTRIX_ENTERPRISE_WRITEBACKS_ENABLED}
              pmsName={t('Dentrix Enterprise')}
              locationIds={dentrixEnterpriseWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.dentrixEnterprise}
            />
            <WritebacksToggle
              setting={LocationFeatureFlagSettingName.DOLPHIN_WRITEBACKS_ENABLED}
              pmsName={t('Dolphin Cloud')}
              locationIds={dolphinCloudWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.dolphinCloud}
            />
            <WritebacksToggle
              setting={LocationFeatureFlagSettingName.DOLPHIN_WRITEBACKS_ENABLED}
              pmsName={t('Dolphin')}
              locationIds={dolphinWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.dolphin}
            />
            <WritebacksToggle
              setting={LocationFeatureFlagSettingName.NEXTECH_SELECT_WRITEBACKS_ENABLED}
              pmsName={t('Nextech Select')}
              locationIds={nextechSelectWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.nextechSelect}
            />
            <WritebacksToggle
              setting={LocationFeatureFlagSettingName.NEXTECH_PRACTICE_WRITEBACKS_ENABLED}
              pmsName={t('Nextech Practice')}
              locationIds={nextechPracticeWritebacksLocations}
              canModifyWritebacks={canModifyWritebacks}
              hasBulkCollection={bulkCollectionsEnabledLocations?.nextechPractice}
            />
          </div>
        )}
        {!!autoReceiptEnabledLocations.length && (
          <div className='section section--receipt-settings'>
            <Heading css={styles.sectionHeading} level={3}>
              {t('Receipt Settings')}
            </Heading>
            <SettingsToggle
              name='toggle-send-receipt-automatically'
              label={t('Send Receipt Automatically')}
              infoText={t(
                'Automatically send a receipt to the patient email on file once a payment request has been paid.'
              )}
              locationIds={autoReceiptEnabledLocations}
              switchProps={receiptSettingsProps}
              hasPermission={canModifyWritebacks}
            />
          </div>
        )}
        <div className='section section--authentication'>
          {!!ttpAuthToggleAllowedLocations.length && (
            <>
              <Heading css={styles.sectionHeading} level={3}>
                {t('Authentication')}
              </Heading>
              <SettingsToggle
                name='enable-ttp-authentication'
                label={t('Require customers to authenticate to view payment requests')}
                infoText={t('Turn on to use birthday for authenticating identity')}
                locationIds={ttpAuthToggleAllowedLocations}
                switchProps={ttpAuthenticationProps}
                hasPermission={canModifyWritebacks}
              />

              <ConfirmationModal
                {...authenticationModalProps}
                title={t('Disable Customer Authentication')}
                confirmLabel={t('Turn Off Authentication')}
                cancelLabel={t('Cancel')}
                onConfirm={onConfirmAuthenticationModal}
                destructive={true}
                maxWidth={parseInt(theme.spacing(76))}
              >
                <Trans t={t}>
                  <Text weight='bold' size='large' css={styles.marginBottom}>
                    By disabling this, your customers will not be required to authenticate to view attachments for
                    payment requests.
                  </Text>
                  <Text size='medium' css={styles.marginBottom}>
                    Disabling this option will no longer require your customers to confirm their identity by entering
                    their birthday to view attachments you have added to their payment requests. This option helps
                    ensure that only authorized individuals have access to sensitive customer documents and/or protected
                    health information.
                  </Text>
                  <Text size='small' css={[styles.marginBottom, styles.confirmModalSmallText]}>
                    Please consult with local and federal laws and compliance guidelines if you have questions or
                    concerns regarding this measure.
                  </Text>
                </Trans>
              </ConfirmationModal>
            </>
          )}
        </div>

        {anyLocationHasFullPayments && (
          <div className='section section--surcharges'>
            <SurchargeSettingsToggle
              percentValue={percentValue}
              onChangePercentValue={onChangePercentValue}
              surchargeSwitchProps={surchargeSwitchProps}
              surchargeEnabledLocations={surchargeEnabledLocations}
              surchargeModalProps={surchargeModalProps}
              onConfirmSurchargeModal={onConfirmSurchargeModal}
              combinedMaxSurchargeInfo={combinedMaxSurchargeInfo}
              hasPermission={canManageSurcharging}
              disabled={!canManageSurcharging}
            />
          </div>
        )}
        {!anyLocationHasFullPayments && (
          <div className='section section--surcharges-no-payments'>
            <SurchargeSettingsNoPayments disabled={!isPaymentsBillingManager} />
          </div>
        )}
      </section>
    </>
  );
};

type WritebacksToggleProps = Pick<SettingsToggleProps, 'locationIds'> & {
  canModifyWritebacks: boolean;
  setting: PaymentsFeatureSettings;
  pmsName: string;
  hasBulkCollection?: boolean;
};

const WritebacksToggle = ({
  pmsName,
  setting,
  locationIds,
  canModifyWritebacks,
  hasBulkCollection,
  ...rest
}: WritebacksToggleProps) => {
  const { t } = useTranslation('payments', { keyPrefix: 'settings' });
  const errorMessage = t('Error in setting the {{pmsName}} writebacks. Please try again.', { pmsName });

  const { featureSettingSwitchProps } = useLocationFeatureSwitch({
    setting,
    errMsg: errorMessage,
    locationIds: locationIds,
  });

  const label = hasBulkCollection
    ? t('Enable bulk collections and payment write-backs to {{pmsName}} Ledger.', { pmsName })
    : t('Write {{pmsName}} Payments to Ledger', { pmsName });
  const fieldName = `enable-${_.kebabCase(pmsName)}-writebacks`;
  const infoText = hasBulkCollection
    ? t(
        'Turn on to enable bulk collecting patient balances and to automatically post successful payments and refunds to the patient ledger in your practice management software. Any payments or refunds made while this setting is toggled off will not automatically post.'
      )
    : t(`Turn on to automatically post successful payments and refunds to the patient ledger in your
    practice management software. Any payments or refunds made while this setting is toggled off
    will not automatically update.`);

  return (
    <SettingsToggle
      switchProps={featureSettingSwitchProps}
      locationIds={locationIds}
      hasPermission={canModifyWritebacks}
      permissionRoleLabel='"modify writeback"'
      label={label}
      infoText={infoText}
      name={fieldName}
      {...rest}
    />
  );
};
